  // src/context/AuthContext.js
  import { createContext, useContext, useEffect, useState } from 'react';
  import {
    login as apiLogin,
    signup as apiSignup,
    verifyTeamCode as apiVerifyTeamCode,
    checkUsername as apiCheckUsername,
    logout as apiLogout,
    verifyToken as apiVerifyToken,
  } from '../api/authAPI';
import { listMemos } from '../api/memoAPI';

  import {
    getToken,
    getUserData,
    clearTokens,
    isTokenExpired,
    handleLoginResponse,
    setUserData,
  } from '../utils/tokenUtils';

  const AuthContext = createContext(null);

  export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // 앱 시작 시 복원 + 토큰 검증으로 user 보강
    useEffect(() => {
      (async () => {
        try {
          const token = getToken();
          if (!token || isTokenExpired(token)) {
            clearAuthData();
          } else {
            // 로컬 user 있으면 우선 반영
            const stored = getUserData();
            if (stored) setUser(stored);
            // 서버로 user 최신화
            try {
              const v = await apiVerifyToken(token); // { success, data: { user } }
              const userData = v?.data?.user || v?.user;
              if (userData) {
                setUser(userData);
                setUserData(userData);
              }
            } catch {
              // 검증 실패하면 세션 정리
              clearAuthData();
            }
          }
        } catch {
          clearAuthData();
        } finally {
          setLoading(false);
          setIsInitialized(true);
        }
      })();
    }, []);

    const login = async (credentials = {}) => {
      try {
        setLoading(true);
        setError(null);

        const username =
          credentials.username || credentials.email || credentials.id;
        const password = credentials.password;
        if (!username || !password)
          throw new Error('아이디와 비밀번호를 입력해주세요.');

        // 로그인 전 이전 사용자 정보 완전 정리
        clearAuthData();

        const data = await apiLogin(username, password); // { token, user? }
        const result = handleLoginResponse(data); // token 저장 + user 저장(있다면)
        if (!result?.success)
          throw new Error(result?.error || '로그인 처리 실패');

        // user가 응답에 없으면 verify로 받아오기
        let u = data.user || null;
        if (!u) {
          try {
            const v = await apiVerifyToken(getToken());
            u = v?.data?.user || v?.user || null;
            if (u) setUserData(u);
          } catch {}
        }
        try {
          const memos = await listMemos({}, getToken());
          console.log('✅ 로그인 시 메모 로드:', memos);

          if (Array.isArray(memos)) {
            u = { ...u, memos };
            console.log(`✅ 총 ${memos.length}개의 메모를 user 객체에 추가했습니다.`);
          } else {
            console.warn('⚠️ 메모 응답이 배열이 아닙니다:', memos);
            u = { ...u, memos: [] };
          }
        } catch (err) {
          console.error('❌ 메모 불러오기 실패:', err);
          u = { ...u, memos: [] };
        }
        setUser(u);

        // ✨ 1. 프로필 완성 여부 판단: user 객체에 playerID이 있는지 확인합니다.
        //    (SignupProfileForm에서 '성명(표시명)'을 playerID으로 저장하기 때문)
        const isProfileComplete = !!(u?.playerID || u?.email || u?.bio);



        return { success: true, user: u, profileComplete: isProfileComplete };
      } catch (e) {
        const msg = parseError(e);
        setError(msg);
        return { success: false, error: msg, profileComplete: false };
      } finally {
        setLoading(false);
      }
    };

    const signup = async (payload) => {
      try {
        setLoading(true);
        setError(null);
        if (!payload?.username || !payload?.password || !payload?.authCode) {
          throw new Error('필수 항목(아이디/비밀번호/인증코드)을 입력해주세요.');
        }
        const res = await apiSignup(payload);
        return {
          success: true,
          data: res,
          message: '회원가입이 완료되었습니다.',
        };
      } catch (e) {
        const msg = parseError(e);
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setLoading(false);
      }
    };

    const logout = async () => {
      try {
        setLoading(true);
        setError(null);
        try {
          await apiLogout();
        } catch {
          /* 서버 실패 무시 */
        }
      } finally {
        clearAuthData();
        setLoading(false);
      }
    };

    const checkUsername = async (username) => {
      try {
        if (!username) return { available: false, error: '아이디를 입력하세요.' };
        const r = await apiCheckUsername(username);
        return { available: !!r.available };
      } catch (e) {
        return { available: false, error: parseError(e) };
      }
    };

    const verifyTeamCode = async (authCode) => {
      try {
        if (!authCode) return { valid: false, error: '인증코드를 입력하세요.' };
        const r = await apiVerifyTeamCode(authCode);
        return { valid: !!r.valid };
      } catch (e) {
        return { valid: false, error: parseError(e) };
      }
    };

    const clearAuthData = () => {
      clearTokens();
      setUser(null);
      setError(null);
    };

    const parseError = (err) => {
      if (!err) return '알 수 없는 오류가 발생했습니다.';
      if (typeof err === 'string') return err;
      if (err.message) return err.message;
      return '알 수 없는 오류가 발생했습니다.';
    };

    const value = {
      user,
      loading,
      error,
      isInitialized,
      isAuthenticated: !!user,
      token: getToken(),

      login,
      signup,
      logout,

      checkUsername,
      verifyTeamCode,

      clearError: () => setError(null),
      
      // 이 함수들 추가
      updateUserData: (userData) => {
        setUser(userData);
        setUserData(userData);
      },
      
      Token: (token) => {
        localStorage.setItem('token', token);
        // 토큰 설정 후 사용자 정보도 새로 가져오기
      },
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  };

  export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx)
      throw new Error('useAuth는 AuthProvider 내부에서만 사용 가능합니다.');
    return ctx;
  };
